import React, { useCallback } from 'react'
import { FileInput, maxLength, useNotify, useRecordContext } from 'react-admin'
import { useWatch, useFormState, useFormContext } from 'react-hook-form'
import { NEWS_FIELDS } from '../../providers/localization/constant'
import { FormHelperText, Box, styled } from '@mui/material'
import { translateErrMessageDropZone } from '../../utils/errorMessage'
import { FilePreviewProps, MultipleFileInputProps } from './type'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import { FileField } from '../fields'

const defaultAcceptFiles = 'image/png, image/jpg, image/jpeg, application/pdf'
const defaultFileInputOptions = {
  maxFiles: 5,
}

const FileInputStyled = styled(FileInput)(({ theme }) => ({
  color: theme.palette.grey[600],
  '& .RaFileInputPreview-removeButton': {
    zIndex: 1,
    position: 'absolute',
  },
}))

const FilePreview: React.FC<FilePreviewProps> = (props) => {
  const { source, fallbackSource, title } = props
  const record = useRecordContext()
  const { src: uploadSrc } = record
  const path = uploadSrc ? source : fallbackSource
  const titleFallback = uploadSrc ? 'title' : title
  return <FileField source={path} title={titleFallback} target='_blank' />
}

export const MultipleFileInput: React.FC<MultipleFileInputProps> = ({
  isApproved = false,
  maxSize = 10000000,
  source = 'files',
  accept = defaultAcceptFiles,
  fileInputProps,
  deleteFileField = null,
  sx = {},
  fallbackSource = null,
  ...props
}) => {
  const { maxFiles, ...otherProps } = { ...defaultFileInputOptions, ...fileInputProps?.options }
  const notify = useNotify()
  const files = useWatch({ name: source })
  const { errors } = useFormState()
  const disabled = files?.length >= maxFiles
  const { getValues, setValue } = useFormContext()

  const disableStyle = disabled
    ? {
        '& .RaFileInput-dropZone': {
          cursor: 'default',
          background: 'transparent',
          border: '1px solid',
          borderColor: errors.files ? 'error.main' : 'common.black',
        },
      }
    : {}

  const baseStyle = {
    '& .MuiFormHelperText-root, .RaLabeled-label, .RaFileInput-dropZone > *': {
      color: errors.files ? 'error.main' : 'unset',
    },
    ...disableStyle,
  }
  const onDropRejected = useCallback(
    (fileRejections): void => {
      notify(translateErrMessageDropZone(fileRejections[0].errors[0].code), { type: 'warning' })
    },
    [notify],
  )

  if (isApproved)
    return (
      <Box>
        <FormHelperText sx={{ mb: 1 }}>{source.toUpperCase()}</FormHelperText>
        <FileField source={source} title={'name'} target='_blank' />
      </Box>
    )

  return (
    <FileInputStyled
      source={source}
      helperText={`${NEWS_FIELDS}.attachment_helper`}
      label={`${NEWS_FIELDS}.attachment`}
      inputProps={{}}
      options={{
        disabled,
        maxFiles,
        onDropRejected,
        ...otherProps,
      }}
      labelMultiple={
        disabled ? 'ra.input.file.upload_max_file_reach' : 'ra.input.file.upload_several'
      }
      onRemove={({ name, rawFile }): void => {
        if (!deleteFileField || !name || rawFile) return
        const prevFields = getValues()
        const targetField = prevFields[deleteFileField]
        const deleteFieldValues = isNil(targetField) || !isArray(targetField) ? [] : targetField
        setValue(deleteFileField, [...deleteFieldValues, name])
      }}
      maxSize={maxSize}
      accept={accept}
      multiple
      validate={[maxLength(maxFiles, 'ra.notification.file_count_exceed_error')]}
      sx={{ ...baseStyle, ...disableStyle, ...sx }}
      {...props}
    >
      <FilePreview source='src' title='name' fallbackSource={fallbackSource} />
    </FileInputStyled>
  )
}
